import { composeThemeFromProps, Compose } from '@css-modules-theme/react';
import maxSize from 'popper-max-size-modifier';
import { forwardRef, useState, useRef, useCallback, useEffect } from 'react';
import mergeRefs from 'react-merge-refs';
import { usePopper } from 'react-popper';
import { config, Transition, animated } from 'react-spring';
import defaultStyles from './styles/Popover.module.scss.js';
import { jsx, jsxs } from 'react/jsx-runtime';
import { Portal } from '../portal/Portal.js';

const availableHeightModifier = {
  name: 'availableHeight',
  enabled: true,
  phase: 'beforeWrite',
  requires: ['maxSize'],
  fn({
    state: {
      modifiersData,
      elements: {
        popper
      }
    }
  }) {
    const {
      height
    } = modifiersData?.maxSize;
    const content = popper.querySelector(`.${defaultStyles.inner}`);
    if (content && !content.style.maxHeight) {
      content.style.maxHeight = `${height}px`;
    }
  }
};
const Popover = /*#__PURE__*/forwardRef((props, ref) => {
  const {
    anchorElement,
    availableHeight = false,
    children,
    dataTestId,
    fallbackPlacements,
    injectionContainer,
    isOpen,
    offset = [0, 0],
    position = 'left',
    preventFlip,
    preventOverflow = true,
    theme,
    update,
    useAnchorWidth = true,
    withArrow = false,
    zIndex = 9999,
    springConfig = {
      from: {
        opacity: 0,
        config: config.stiff
      },
      to: {
        opacity: 1,
        config: config.stiff
      }
    }
  } = props;
  const styles = composeThemeFromProps(defaultStyles, {
    theme
  }, {
    compose: Compose.Merge
  });
  const [referenceElement, setReferenceElement] = useState(anchorElement);
  const [popperElement, setPopperElement] = useState(null);
  const updatePopperRef = useRef();
  const arrowRef = useRef(null);
  const getModifiers = useCallback(() => {
    const modifiers = [{
      name: 'offset',
      options: {
        offset
      }
    }];
    if (withArrow) {
      modifiers.push({
        name: 'arrow',
        options: {
          element: arrowRef?.current,
          padding: 10
        }
      });
    }
    if (preventFlip) {
      modifiers.push({
        name: 'flip',
        options: {
          fallbackPlacements: []
        }
      });
    }
    if (fallbackPlacements) {
      modifiers.push({
        name: 'flip',
        options: {
          fallbackPlacements
        }
      });
    }
    if (preventOverflow) {
      modifiers.push({
        name: 'preventOverflow',
        options: {
          mainAxis: true
        }
      });
    }
    if (availableHeight) {
      modifiers.push({
        ...maxSize,
        options: {}
      });
      modifiers.push({
        ...availableHeightModifier,
        options: {}
      });
    }
    return modifiers;
  }, [offset, withArrow, preventFlip, fallbackPlacements, preventOverflow, availableHeight, arrowRef]);
  const {
    styles: popperStyles,
    attributes,
    update: updatePopper
  } = usePopper(referenceElement, popperElement, {
    placement: position,
    modifiers: getModifiers()
  });
  if (updatePopper) {
    updatePopperRef.current = updatePopper;
  }
  useEffect(() => {
    setReferenceElement(anchorElement);
  }, [anchorElement]);
  useEffect(() => {
    if (updatePopper) {
      updatePopper();
    }
  }, [updatePopper, arrowRef, children]);
  useEffect(() => {
    if (update && !update.current && updatePopper) {
      // eslint-disable-next-line no-param-reassign
      update.current = updatePopper;
    }
  });
  const renderContent = computedZIndex => {
    return /*#__PURE__*/jsx(Portal, {
      injectionContainer: injectionContainer,
      children: /*#__PURE__*/jsx(Transition, {
        native: true,
        items: isOpen,
        from: springConfig.from,
        enter: springConfig.to,
        leave: springConfig.from,
        children: (transitionStyles, item) => item && /*#__PURE__*/jsx(animated.div, {
          ref: mergeRefs([setPopperElement, ref]),
          style: {
            zIndex: computedZIndex,
            width: useAnchorWidth ? anchorElement?.offsetWidth : undefined,
            ...transitionStyles,
            ...popperStyles.popper
          },
          "data-popper": true,
          className: styles.root,
          ...attributes.popper,
          "data-test-id": dataTestId,
          children: /*#__PURE__*/jsxs("div", {
            className: styles.inner,
            children: [children, " ", withArrow && /*#__PURE__*/jsx("div", {
              ref: arrowRef,
              style: popperStyles.arrow,
              className: styles.arrow
            })]
          })
        })
      })
    });
  };
  return renderContent(zIndex);
});

export { Popover };
